import React, { useEffect, useImperativeHandle, useRef, useState, useCallback, useMemo, useContext } from 'react';
import { message, Popover } from 'antd';
import debounce from 'lodash/debounce';
import classnames from 'classnames';
import dayjs from 'dayjs';
import juice from 'juice';
import './MailContent.scss';
import { SelectReceiptModal } from '@lxunit/app-l2c-crm';
import '@lxunit/app-l2c-crm/lib.css';
import { apiHolder as api, apis, ProductAuthorityFeature, inWindow, EntSignatureDisplayType, shouldSkipPersonalSign, } from 'api';
import { ContactActions, DiskAttActions, MailActions, MailConfigActions, MailTemplateActions, useActions, useAppDispatch, useAppSelector, } from '@web-common/state/createStore';
import { doAddDiskAttachment } from '@web-common/state/reducer/diskAttReducer';
import Loadable from '@loadable/component';
const SelectSign = Loadable(() => import('@web-setting/Mail/components/CustomSignForm/select_sign_modal/index'));
const SignEdit = Loadable(() => import('@web-setting/Mail/components/CustomSignForm/sign_edit_modal/index'));
if (inWindow()) {
    setTimeout(() => {
        if (window.requestIdleCallback) {
            window.requestIdleCallback(() => {
                SelectSign.preload();
                window.requestIdleCallback(() => {
                    SignEdit.preload();
                });
            });
        }
        else {
            SelectSign.preload();
            setTimeout(() => {
                SignEdit.preload();
            }, 3000);
        }
    }, 15000);
}
import { createNiceModal } from '@web-common/components/UI/NiceModal/NiceModal';
import { getSignTemplatesAsync } from '@web-common/state/reducer/mailConfigReducer';
import { MailAt } from '../MailAt';
import { DiskAttachmentModal } from '@web-mail/components/DiskAttachmentModal';
import LxPopover from '@web-common/components/UI/LxPopover/LxPopover';
import ContactDetail from '@web-contact/component/Detail/detail';
import { useEventObserver } from '@web-common/hooks/useEventObserver';
import useMsgRenderCallback from '@web-common/hooks/useMsgRenderCallback';
import { scaleEditor, filterSVGScript, contentAssitantEditor } from './tools';
import { MAIL_EDITOR_CONTAINER_CLASS, MAIL_EDITOR_CONTAINER_EDM_CLASS } from './editor-config';
import { useNiceModal } from '@web-common/components/UI/NiceModal/NiceModal';
import { transContactModel2MailContactModel } from '@web-common/utils/contact_util';
import { BaseWrite as LxEditor } from '@web-common/components/UI/LxEditor/LxEditor';
import { ProductSettingModal } from '@web-edm/components/productSettingModal/productSettingModal';
import { LandingPageModal } from '@web-edm/components/landingPageModal/landingPageModal';
import ImgProduct from '@web-edm/components/editor/imgProduct';
import { getProductsHtml, getLandingPageHtml } from '@web-edm/components/editor/template';
import SalesPitchWritePageDrawer from '@web-mail-write/components/MailContent/SalesPitchWritePageDrawer/index';
import useState2SalesPitchReduxMock from '@/components/Layout/EnterpriseSetting/salesPitch/utils/useState2ReduxMock';
import { SALES_PITCH_GUIDE_ID } from '@/components/Layout/EnterpriseSetting/salesPitch/components/salesPitchGuide';
import useDebounceForEvent from '@web-mail/hooks/useDebounceForEvent';
import { proxyImg, systemIsMac } from '@web-mail/util';
import useFeatureVisibility from '@web-common/hooks/useFeatureVisibility';
import { getIfHaveAuth } from '@web-common/utils/utils';
import { getIn18Text } from 'api';
import { MailForwardContent, MailPreMailContent } from '@web-mail/utils/mailClassnameConstant';
import { useSignSiteGuide, SignSiteGuide } from '@web-common/components/UI/LxEditor/SignSiteGuide/index';
import { WriteContext } from '@web-setting/Mail/components/CustomTemplate/template_add_modal';
import { useHidePersonalSignEntry } from '../../hooks/useHidePersonalSignEntry';
const SignEditModal = createNiceModal('signEditOnWrite', SignEdit);
const SelectSignModal = createNiceModal('selectSignOnWrite', SelectSign);
const storeApi = api.api.getDataStoreApi();
const systemApi = api.api.getSystemApi();
const isElectron = systemApi.isElectron();
const inEdm = systemApi.inEdm();
const trackApi = api.api.requireLogicalApi(apis.dataTrackerApiImp);
const accountApi = api.api.requireLogicalApi(apis.accountApiImpl);
const performanceApi = api.api.requireLogicalApi(apis.performanceImpl);
const contactApi = api.api.requireLogicalApi(apis.contactApiImpl);
const mailConfApi = api.api.requireLogicalApi(apis.mailConfApiImpl);
const eventApi = api.api.getEventApi();
const env = process.env.NODE_ENV;
const mailSigApi = api.api.requireLogicalApi(apis.mailSignatureImplApi);
const productAuthApi = api.api.requireLogicalApi(apis.productAuthApiImpl);
const tinyStyle = 'body {margin: 0; font-size: 14px; overflow: hidden; line-height: 1.5;}';
const isMac = systemIsMac();
let toolbar = [
    'lxuploadattachment $split lxaiwritemail $split lxmailformat $split lxsignature $split preview print code $split undo redo lxformatpainter removeformat',
    [
        'fontselect fontsizeselect',
        'bold italic underline strikethrough forecolor backcolor',
        'bullist numlist lineheight',
        'lxgrammar',
        'link lxemoji',
        'alignleftSplit dentSplit lxinputdirection',
        `lxTable lximg ${isElectron ? 'lxcapturescreen' : ''} lxsplitline`,
    ].join(' $split '),
];
if (inEdm) {
    toolbar = [
        [
            'lxutils',
            'lxuploadattachment',
            'lxaiwritemail',
            'undo redo lxformatpainter removeformat ',
            'fontselect fontsizeselect',
            'bold italic underline strikethrough forecolor backcolor',
            'bullist numlist lineheight',
            'lxgrammar',
            'alignleftSplit dentSplit lxinputdirection',
            `lxTable lximg ${isElectron ? 'lxcapturescreen ' : ''}link lxemoji`,
            'preview print code lxsplitline',
        ].join(' $split '),
    ];
}
let toolbar_mode = 'floating';
const MailContent = React.forwardRef((props, ref) => {
    var _a;
    const [, setGuideVisible] = useState2SalesPitchReduxMock('guideVisible');
    const [, setWritePageGuidePos] = useState2SalesPitchReduxMock('writePageGuidePos');
    const [, setWritePageOuterDrawerVisible] = useState2SalesPitchReduxMock('writePageOuterDrawerVisible');
    const { uploadAttachmentAction, mailFormatAction, onEditCreated, uploadAttachmentWithFiles, recognizeDiskUrl, webScale, showAssistant, uploadAttachment, sensitiveWords, enableSensitiveMarks, sensitiveChecking, onSensitiveDetectedChange, onToolbarHeightChange, pasteAtt, } = props;
    const mailApi = api.api.requireLogicalApi(apis.mailApiImpl);
    const currentMail = useAppSelector(state => state.mailReducer.currentMail);
    const infoStatus = useAppSelector(state => state.mailReducer.currentMail.status);
    const conferenceSetting = useRef(false);
    const taskSetting = useRef(false);
    const praiseMailSetting = useRef(false);
    const sensitiveSetting = useRef(false);
    const focusedRef = useRef('');
    conferenceSetting.current = infoStatus === null || infoStatus === void 0 ? void 0 : infoStatus.conferenceSetting;
    taskSetting.current = infoStatus === null || infoStatus === void 0 ? void 0 : infoStatus.taskMailSetting;
    praiseMailSetting.current = infoStatus === null || infoStatus === void 0 ? void 0 : infoStatus.praiseMailSetting;
    sensitiveSetting.current = infoStatus === null || infoStatus === void 0 ? void 0 : infoStatus.sensitiveTipShow;
    const currentMailContent = useAppSelector(state => { var _a, _b; return (_b = (_a = state.mailReducer.currentMail) === null || _a === void 0 ? void 0 : _a.entry) === null || _b === void 0 ? void 0 : _b.content.content; });
    const title = useAppSelector(state => { var _a, _b; return (_b = (_a = state.mailReducer.currentMail) === null || _a === void 0 ? void 0 : _a.entry) === null || _b === void 0 ? void 0 : _b.title; });
    const focused = useAppSelector(state => state.contactReducer.selector.focused);
    focusedRef.current = focused;
    const waittingMailIds = useAppSelector(state => state.mailReducer.waittingMailIds);
    const curAccount = useAppSelector(state => { var _a; return (_a = state.mailReducer.currentMail) === null || _a === void 0 ? void 0 : _a.optSender; });
    const curAccountRef = useRef(curAccount);
    const textareaRef = useRef(null);
    curAccountRef.current = curAccount;
    const [localSender, setLocalSender] = useState();
    const currentMailId = useAppSelector(state => { var _a; return (_a = state.mailReducer.currentMail) === null || _a === void 0 ? void 0 : _a.cid; });
    const defaultSign = useAppSelector(state => { var _a; return (_a = state.mailReducer.currentMail) === null || _a === void 0 ? void 0 : _a.defaultSign; });
    const puretext = useAppSelector(state => { var _a; return (_a = state.mailReducer.currentMail.status) === null || _a === void 0 ? void 0 : _a.puretext; });
    const menus = useAppSelector(state => state.privilegeReducer.menus);
    const resetCont = useMemo(() => currentMail.resetCont, [currentMail]);
    const dispatch = useAppDispatch();
    const { doChangeMailContent, doChangeMailScrollBoxTop, setResetCont, doConferenceSettting, doModifyReceiver, doTaskMailSetting, doPraiseMailSetting, doUpdateForbidSaveTemp, doChangeCacheAttachment, doModifyDefaultSign, doSetWirteMailSpin, doModifySubject, doMailImgSizeChange, doShowSensitiveTip, } = MailActions;
    const { doSetDisplayMail, doSetSignList } = MailConfigActions;
    const [content, setContent] = useState('');
    const [delaySet, setDelaySet] = useState(false);
    const [delaySetSuc, setDelaySetSuc] = useState(false);
    const [uploadAttachmentConfig, setUploadAttachmentConfig] = useState(null);
    const [haveContent, setHaveContent] = useState(false);
    const currentMailIdRef = useRef(currentMailId);
    currentMailIdRef.current = currentMailId;
    const currentMailRef = useRef(currentMail);
    currentMailRef.current = currentMail;
    const refContainer = useRef(null);
    const senderEmail = useMemo(() => (currentMail === null || currentMail === void 0 ? void 0 : currentMail.initSenderStr) || '', [currentMailIdRef.current, currentMail === null || currentMail === void 0 ? void 0 : currentMail.initSenderStr]);
    const senderEmailRef = useRef(senderEmail);
    senderEmailRef.current = senderEmail;
    const refEditorInstance = useRef(null);
    const [layerZIndex, setLayerZIndex] = useState(1);
    const [editorInstance, setEditorInstance] = useState(null);
    const getAtResultCBRef = useRef(() => { });
    const [token, setToken] = useState('');
    const [imgProductVisible, setImgProductVisible] = useState(false);
    const selectSignModalAttr = useNiceModal('selectSignOnWrite');
    const [showSettingProductModal, setShowSettingProductModal] = useState(false);
    const [showSettingLandingModal, setShowSettingLandingModal] = useState(false);
    const [showSiteGuideState, setShowSiteGuideState] = useState(false);
    const { signDOMRect, signLinkHandlers } = useSignSiteGuide({
        showSiteGuideState: showSiteGuideState,
        setShowSiteGuideState: setShowSiteGuideState,
    });
    const [receiptConfig, setReceiptConfig] = useState({
        modalType: undefined,
        trackType: 'mail',
        open: false,
    });
    const [fromImgProduct, setFromImgProduct] = useState(false);
    const [loadEd, setLoadEd] = useState(false);
    const hideSignEntry = useHidePersonalSignEntry(senderEmail, mailConfApi, systemApi, currentMailId);
    const hideSignEntryRef = useRef(hideSignEntry);
    hideSignEntryRef.current = hideSignEntry;
    useEffect(() => {
        editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.fire('lxToggleSignEntry', { hide: hideSignEntry });
    }, [hideSignEntry, editorInstance, currentMailId]);
    useEffect(() => {
        setTimeout(() => {
            setLoadEd(true);
        }, 10 * 1000);
    }, []);
    useEffect(() => {
        if (delaySetSuc)
            return;
        if (delaySet && editorInstance && content) {
            setDelaySetSuc(true);
            setTimeout(() => {
                editorInstance.setContent(content);
            }, 1000);
        }
    }, [delaySet, editorInstance, content]);
    const { isMailTemplate } = useContext(WriteContext);
    useEffect(() => {
        if (showSiteGuideState) {
            trackApi.track('pc_mail_signature_site_guide_view', {
                type: '1',
            });
        }
    }, [showSiteGuideState]);
    useEffect(() => {
        accountApi.doGetMailAliasAccountListV2({ noMain: true });
    }, []);
    useEffect(() => {
        if (inEdm) {
            toolbar = [
                [
                    'lxutils $split lxuploadattachment $split lxaiwritemail',
                    'undo redo lxformatpainter removeformat ',
                    'fontselect fontsizeselect',
                    'bold italic underline strikethrough forecolor backcolor',
                    'bullist numlist lineheight',
                    'lxgrammar',
                    'alignleftSplit dentSplit lxinputdirection',
                    `lxTable lximg ${isElectron ? 'lxcapturescreen ' : ''}link lxemoji`,
                    'preview print code lxsplitline',
                ].join(' $split '),
            ];
        }
        if (inEdm && menus.length == 0) {
            toolbar[0] = toolbar[0].replace('lxappendproduct lxproducttip $split lxappendlandingpage lxlandingpagetip $split lxsitelinkbtn lxsitelinkbtntip $split', '');
        }
        systemApi.doGetCookies(true).then(cookies => {
            const { QIYE_TOKEN } = cookies;
            setToken(QIYE_TOKEN);
        });
    }, [menus.length]);
    useEffect(() => {
        if (editorInstance) {
            editorInstance.on('beforeDeleteImage', e => {
                const imgsSize = Array.from((e === null || e === void 0 ? void 0 : e.images) || [])
                    .map(img => {
                    var _a;
                    const attachSize = (_a = img === null || img === void 0 ? void 0 : img.getAttribute('src')) === null || _a === void 0 ? void 0 : _a.match(/attachSize=(\d+)/);
                    if (attachSize && attachSize[1]) {
                        return parseInt(attachSize[1]);
                    }
                    return 0;
                })
                    .reduce((prev, next) => prev - next, 0);
                dispatch(doMailImgSizeChange({ count: imgsSize || 0 }));
            });
        }
    }, [editorInstance]);
    const getToken = () => token;
    const [contactPos, setContactPos] = useState({
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
    });
    const [atVisible, setAtVisible] = useState(false);
    const [contactCardVisible, setContactCardVisible] = useState(false);
    const [contactClicked, setContactClicked] = useState();
    const { changeShowTemplateList } = useActions(MailTemplateActions);
    const preSetDign = (signHtml) => {
        if (!signHtml) {
            return '';
        }
        const signJuice = juice(signHtml);
        const lastVal = signJuice.replace(/\n/g, '');
        return lastVal;
    };
    const editorChangeSign = async (sign) => {
        const editorVal = preSetDign(sign);
        let entSignaturePosition;
        try {
            const entSig = await mailConfApi.getEntSignature(true, curAccount === null || curAccount === void 0 ? void 0 : curAccount.email);
            entSignaturePosition = entSig === null || entSig === void 0 ? void 0 : entSig.isDefault;
        }
        catch (e) {
            console.warn('获取企业签名位置失败', e);
        }
        if (editorVal && editorVal.trim()) {
            editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.fire('modifySignature', {
                sigContent: mailApi.doTransferSign(`<div class='mail-signature' onselectstart="return false">${editorVal}</div>`),
                entSignaturePosition,
            });
        }
        else {
            editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.fire('modifySignature', { sigContent: '' });
        }
        if (editorInstance) {
            dispatch(doChangeMailContent((editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.getContent()) || ''));
        }
    };
    const mailInsertContent = contentParam => {
        editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.insertContent(contentParam);
        handleSensitiveMarksUpdate('mailInsertContent');
    };
    const insertTemplateToEditor = (templateContent) => {
        if (!props.isContentChanged) {
            props.onEditChange();
        }
        mailInsertContent(templateContent);
        editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.fire('updatePlaceholder', { show: false });
    };
    useImperativeHandle(ref, () => ({
        forceSave: () => {
            var _a;
            return (_a = refEditorInstance === null || refEditorInstance === void 0 ? void 0 : refEditorInstance.current) === null || _a === void 0 ? void 0 : _a.getContent();
        },
        insertTemplateToEditor,
        getEditorInstance: () => refEditorInstance.current,
        insertVariable: (key, value) => {
            handleVariableChange(key, value);
        },
        appendProductAction,
    }));
    const getDefaultSign = async (updateEditor, mailEmail) => {
        var _a, _b;
        console.log('getDefaultSign2in');
        try {
            const defaultSignRes = await mailSigApi.doGetDefaultSign(true, (_a = currentMail === null || currentMail === void 0 ? void 0 : currentMail.entry) === null || _a === void 0 ? void 0 : _a.writeLetterProp, mailEmail);
            if (refEditorInstance.current) {
                if (systemApi.getIsCorpMailMode()) {
                    refEditorInstance.current.fire('lxToggleSignEntry', { hide: false });
                }
                else {
                    try {
                        const entSig = await mailConfApi.getEntSignature(true, mailEmail);
                        refEditorInstance.current.fire('lxToggleSignEntry', { hide: shouldSkipPersonalSign(entSig) });
                    }
                    catch (_e) {
                        refEditorInstance.current.fire('lxToggleSignEntry', { hide: false });
                    }
                }
            }
            console.log('changesendersign1', defaultSignRes);
            if ((updateEditor || defaultSignRes.defaultSignChange) && defaultSignRes.enable) {
                const lastVal = preSetDign(defaultSignRes.content);
                console.log('changesendersign2', lastVal);
                (_b = refEditorInstance.current) === null || _b === void 0 ? void 0 : _b.fire('allSignature', {
                    sigContent: mailApi.doTransferSign(lastVal),
                });
            }
            if (refEditorInstance.current && defaultSignRes && defaultSignRes.display === EntSignatureDisplayType.ReadOnly) {
                refEditorInstance.current.fire('lximmutable:enable', { lockedSelector: '.mail-signature-ent' });
            }
            const signList = defaultSignRes.personalSignList;
            signList === null || signList === void 0 ? void 0 : signList.map(sign => {
                sign._account = mailEmail;
            });
            signList && dispatch(doSetSignList(signList));
            delete defaultSignRes.personalSignList;
            dispatch(doModifyDefaultSign({ cid: currentMail.cid, sign: defaultSignRes }));
        }
        catch (e) {
            console.log('changesendersign0', e);
        }
    };
    useEffect(() => {
        console.log('getDefaultSign2effect', [
            curAccount,
            localSender,
            curAccount === null || curAccount === void 0 ? void 0 : curAccount.mailEmail,
            localSender === null || localSender === void 0 ? void 0 : localSender.mailEmail,
            curAccount === null || curAccount === void 0 ? void 0 : curAccount.currentMailCid,
            localSender === null || localSender === void 0 ? void 0 : localSender.currentMailCid,
        ]);
        if (curAccount &&
            localSender &&
            curAccount.mailEmail !== localSender.mailEmail) {
            setTimeout(() => {
                console.log('getDefaultSign2effectin');
                getDefaultSign(true, curAccount.mailEmail);
            }, 1000);
        }
    }, [curAccount]);
    useEffect(() => {
        if (curAccount && curAccount.mailEmail !== (localSender === null || localSender === void 0 ? void 0 : localSender.mailEmail)) {
            setLocalSender(curAccount);
            dispatch(doSetDisplayMail(curAccount.agentEmail || ''));
            dispatch(getSignTemplatesAsync());
        }
    }, [curAccount]);
    const [sensitiveMarks, setSensitiveMarks] = useState([]);
    const [sensitiveLayerScrollX, setSensitiveLayerScrollX] = useState(0);
    const getSensitiveDetectedIndex = (word) => (props.sensitiveWordsDetected ? props.sensitiveWordsDetected.findIndex(wordItem => wordItem.word === word) + 1 : 0);
    const getTextNodesByDocument = (document) => {
        if (!document)
            return [];
        const textNodes = [];
        const loop = (node) => {
            var _a;
            const isText = node.nodeType === Node.TEXT_NODE;
            const isElement = node.nodeType === Node.ELEMENT_NODE;
            if (isText) {
                textNodes.push(node);
            }
            else if (isElement) {
                const ele = node;
                const blackTagList = ['style', 'title', 'script'];
                const blackIdList = [MailForwardContent];
                const tagBool = blackTagList.includes(ele.tagName.toLocaleLowerCase());
                const classBool = ele.classList.contains(MailPreMailContent);
                const idBool = blackIdList.includes(ele.id.toLocaleLowerCase());
                if (!tagBool && !classBool && !idBool && ((_a = node.childNodes) === null || _a === void 0 ? void 0 : _a.length)) {
                    node.childNodes.forEach(child => {
                        loop(child);
                    });
                }
            }
        };
        loop(document.body);
        return textNodes;
    };
    const handleSensitiveCore = (document) => {
        var _a;
        const documentRect = ((_a = document === null || document === void 0 ? void 0 : document.documentElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) || new DOMRect(0, 0, 0, 0);
        const documentTextNodes = getTextNodesByDocument(document);
        const sensitiveMarkMap = {};
        const nextSensitiveWordsDetected = [];
        if (!sensitiveWords || sensitiveWords.length === 0 || documentTextNodes.length === 0) {
            return;
        }
        const markTextNode = (textNode) => {
            const nodeValue = textNode.nodeValue || '';
            sensitiveWords.forEach(wordItem => {
                const { word } = wordItem;
                let wordForRegExp = '';
                if (/[a-zA-Z]/.test(word)) {
                    wordForRegExp = '\\b' + word.replace(/[\[\]\(\)\{\}\.\*\+\?\^\$\\\|]/g, $1 => `\\${$1}`).trim() + '\\b';
                }
                else {
                    wordForRegExp = '\\b' + word.replace(/[\[\]\(\)\{\}\.\*\+\?\^\$\\\|]/g, $1 => `\\${$1}`).trim();
                }
                const wordMatched = Array.from(nodeValue.matchAll(new RegExp(wordForRegExp, 'gi')));
                if (!wordMatched.length)
                    return;
                const wordRanges = wordMatched.map(item => ({
                    start: item.index,
                    end: item.index + word.length,
                }));
                if (!nextSensitiveWordsDetected.includes(wordItem)) {
                    nextSensitiveWordsDetected.push(wordItem);
                }
                wordRanges.forEach(({ start, end }) => {
                    const range = typeof Range === 'function' ? new Range() : document.createRange();
                    range.setStart(textNode, start);
                    range.setEnd(textNode, end);
                    const letter = range.getBoundingClientRect();
                    const letterMark = {
                        width: letter.width,
                        height: letter.height,
                        top: letter.top - documentRect.y,
                        left: letter.left - documentRect.x,
                        isFirstNode: true,
                        word,
                    };
                    if (sensitiveMarkMap[word]) {
                        sensitiveMarkMap[word].push(letterMark);
                    }
                    else {
                        sensitiveMarkMap[word] = [letterMark];
                    }
                });
            });
        };
        documentTextNodes.forEach(markTextNode);
        const nextSensitiveMarks = Object.keys(sensitiveMarkMap).reduce((accumulator, word) => [...accumulator, ...sensitiveMarkMap[word]], []);
        return {
            nextSensitiveMarks,
            nextSensitiveWordsDetected,
        };
    };
    const handleSensitiveMarksUpdate = useDebounceForEvent((from) => {
        var _a;
        console.log('[handleSensitiveMarksUpdate] exec', from);
        if (enableSensitiveMarks) {
            console.time('[handleSensitiveMarksUpdate]');
            const document = (_a = refEditorInstance.current) === null || _a === void 0 ? void 0 : _a.getDoc();
            const res = handleSensitiveCore(document);
            const { nextSensitiveMarks, nextSensitiveWordsDetected } = res || {
                nextSensitiveMarks: [],
                nextSensitiveWordsDetected: [],
            };
            setSensitiveMarks(nextSensitiveMarks);
            onSensitiveDetectedChange && onSensitiveDetectedChange(nextSensitiveWordsDetected);
            console.timeEnd('[handleSensitiveMarksUpdate]');
        }
    }, 2000, { leading: false, trailing: true, maxWait: 30 * 1000 });
    useEffect(() => {
        handleSensitiveMarksUpdate('useEffect');
    }, [sensitiveWords, enableSensitiveMarks]);
    const writeMailLoadTrack = () => {
        var _a;
        performanceApi.timeEnd({
            statKey: 'write_mail_load',
            statSubKey: `${currentMail === null || currentMail === void 0 ? void 0 : currentMail.cid}`,
            params: {
                type: `${(_a = currentMail === null || currentMail === void 0 ? void 0 : currentMail.entry) === null || _a === void 0 ? void 0 : _a.writeLetterProp}`,
            },
        });
    };
    const findActionNode = () => {
        const nodes = document.querySelectorAll('.tox-toolbar__group[role=toolbar]');
        if (nodes) {
            return Array.from(nodes).find(node => node.innerHTML.includes('跟单话术库'));
        }
        return null;
    };
    const getCurrentMailImgSize = () => {
        var _a;
        const imgs = ((_a = refEditorInstance.current) === null || _a === void 0 ? void 0 : _a.getDoc().querySelectorAll('img')) || [];
        const imgsSize = Array.from(imgs)
            .map(img => {
            var _a;
            const attachSize = (_a = img.getAttribute('src')) === null || _a === void 0 ? void 0 : _a.match(/attachSize=(\d+)/);
            if (attachSize && attachSize[1]) {
                return parseInt(attachSize[1]);
            }
            return 0;
        })
            .reduce((prev, next) => prev + next, 0);
        dispatch(doMailImgSizeChange({ type: 'cover', count: imgsSize }));
    };
    const setEditor = useCallback(debounce(() => {
        var _a, _b, _c;
        const contentTemp = (_c = (_b = (_a = currentMailRef === null || currentMailRef === void 0 ? void 0 : currentMailRef.current) === null || _a === void 0 ? void 0 : _a.entry) === null || _b === void 0 ? void 0 : _b.content) === null || _c === void 0 ? void 0 : _c.content;
        if (contentTemp !== undefined) {
            setHaveContent(true);
        }
        currentMailIdRef.current = currentMailId;
        if (editorInstance) {
            editorInstance.setContent(contentTemp, {
                after: () => {
                    if (defaultSign === undefined) {
                        getDefaultSign(false, (currentMail === null || currentMail === void 0 ? void 0 : currentMail.mailFormClickWriteMail) || senderEmail);
                    }
                    if (focused === 'focusContent') {
                        editorInstance.focus();
                    }
                    writeMailLoadTrack();
                    getCurrentMailImgSize();
                    const scrollBox = document.querySelector('#writeMailEditorScroll');
                    if (scrollBox) {
                        setTimeout(() => {
                            scrollBox.scrollTop = (currentMail === null || currentMail === void 0 ? void 0 : currentMail.scrollBoxTop) || 0;
                        }, 100);
                    }
                },
            });
        }
        else {
            setDelaySet(true);
        }
        contentTemp && setContent(contentTemp);
        setTimeout(() => {
            var _a;
            const parentEl = (_a = refContainer.current) === null || _a === void 0 ? void 0 : _a.parentElement;
            if (parentEl && parentEl.scrollTo) {
                parentEl.scrollTo(0, 0);
            }
        }, 100);
        if (refContainer.current && currentMail.entry) {
            const iframeDom = refContainer.current.querySelector('iframe');
        }
    }, 300), [currentMail, defaultSign]);
    useEffect(() => {
        try {
            const config = getIfHaveAuth(ProductAuthorityFeature.ORG_SETTING_BIG_ATTACH_SHOW) ? [0, 1, 2, 3] : [0, 2, 3];
            setUploadAttachmentConfig(config);
        }
        catch (error) {
            console.log('获取云附件权限失败', error);
            setUploadAttachmentConfig([0, 1, 2, 3]);
        }
    }, []);
    useEffect(() => {
        setEditor();
        setSensitiveMarks([]);
        onSensitiveDetectedChange && onSensitiveDetectedChange([]);
        dispatch(doUpdateForbidSaveTemp(false));
        setShowSiteGuideState(false);
        signLinkHandlers(refEditorInstance.current, 'writeMail');
        setImgProductVisible(false);
    }, [currentMailId]);
    const titleRef = useRef(title);
    useEffect(() => {
        titleRef.current = title;
    }, [title]);
    useEffect(() => {
        const eventID = eventApi.registerSysEventObserver('assistantContextChanged', {
            func: event => {
                if (event.eventStrData === 'tinymce') {
                    if (!titleRef.current) {
                        dispatch(doModifySubject(event.eventData.subject));
                    }
                    editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.fire('updatePlaceholder', { show: false });
                    if (event.eventData.action === 'replace') {
                        editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.fire('replaceContentBeforeSign', { content: event.eventData.defaultContent });
                        return;
                    }
                    editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.fire('insertContentBeforeSign', { content: event.eventData.defaultContent });
                }
            },
        });
        return () => {
            eventApi.unregisterSysEventObserver('assistantContextChanged', eventID);
        };
    }, [editorInstance]);
    useEffect(() => {
        if (resetCont) {
            dispatch(setResetCont(false));
            setEditor();
        }
    }, [resetCont]);
    useEventObserver('electronClosed', {
        name: 'mailContentElectronClosedOb',
        func: () => {
            var _a;
            dispatch(doUpdateForbidSaveTemp(true));
            console.log('[save draft] closed');
            const hasReceiver = ((_a = currentMail === null || currentMail === void 0 ? void 0 : currentMail.receiver) === null || _a === void 0 ? void 0 : _a.length) > 0;
            if (hasReceiver) {
                editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.focus();
            }
        },
    });
    useMsgRenderCallback('writePageDataExchange', ev => {
        if ((ev === null || ev === void 0 ? void 0 : ev.eventStrData) === 'inlineImgReUpload') {
            console.log('编辑器图片替换', ev.eventData);
            editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.fire('replaceImgUrl', ev.eventData);
        }
    });
    const toobarHeight = useRef(78);
    const checkToolbarHeightChange = () => {
        const toolbarDom = document.querySelector('.tox-editor-header>.tox-toolbar-overlord');
        if (toolbarDom && toolbarDom.clientHeight) {
            if (toolbarDom.clientHeight !== toobarHeight.current) {
                toobarHeight.current = toolbarDom.clientHeight;
                onToolbarHeightChange && onToolbarHeightChange(toolbarDom.clientHeight);
            }
        }
    };
    const debounceInterrupt = useDebounceForEvent(() => {
        if (currentMailIdRef.current && waittingMailIds.includes(currentMailIdRef.current)) {
            dispatch(MailActions.doRemWaittingMailId(currentMailIdRef.current));
            message.error({ content: getIn18Text('ZIDONGFASONGZHONGD，QBJWCHCXFS') });
        }
    }, 500);
    const changeEditor = e => {
        debounceInterrupt();
        handleSensitiveMarksUpdate('changeEditor');
        signLinkHandlers(refEditorInstance.current, 'writeMail');
    };
    const debounceSilentSaveDraft = (params) => {
        let curCid = currentMailIdRef.current;
        if (params === null || params === void 0 ? void 0 : params.curCid)
            curCid = params === null || params === void 0 ? void 0 : params.curCid;
        eventApi.sendSysEvent({
            eventName: 'toSaveDraft',
            eventStrData: '',
            eventData: {
                type: 'localAndRemote',
                trigger: 'attachChange',
                mailId: curCid,
            },
        });
    };
    const texChangeDebounceSave = useDebounceForEvent(() => {
        eventApi.sendSysEvent({
            eventName: 'mailMenuOper',
            eventData: {},
            eventStrData: 'saveDraft',
        });
    }, 1000, {
        leading: true,
        trailing: false,
    });
    const imagesUploadHandler = async (blobInfo, succFun, failFun) => {
        const imgFile = blobInfo.blob();
        if (inEdm) {
            storeApi.get('imgProductDate').then(res => {
                if (res.data === '32') {
                    return;
                }
                if (res.data === JSON.stringify(dayjs().date())) {
                    return;
                }
                trackApi.track('pc_mail_picture_guide_view', { type: 1 });
                setImgProductVisible(true);
            });
        }
        const curCid = currentMailIdRef.current;
        if (Object.prototype.toString.call(imgFile) === '[object File]') {
            filterSVGScript(imgFile).then(async (file) => {
                const fileUrl = await imagesUploadService(file, succFun, failFun);
                dispatch(doChangeCacheAttachment({
                    id: currentMailIdRef.current,
                    type: 'inlineImg',
                    value: imgFile,
                    operationType: 'add',
                    originFileUrl: fileUrl,
                }));
                debounceSilentSaveDraft({ curCid });
            });
        }
    };
    const imagesUploadService = async (imgFile, succFun, failFun) => {
        var _a;
        const accounts = await accountApi.getMainAndSubAccounts();
        const mainAccount = accounts[0].mainAccount;
        const flag = { inline: true };
        const _token = ((_a = mailConfApi.accountTokens.find(token => token.account === senderEmailRef.current)) === null || _a === void 0 ? void 0 : _a.token) || '';
        const isSubAccount = mailConfApi.isSubAccount(senderEmailRef.current);
        if (mainAccount !== senderEmailRef.current) {
            flag._account = senderEmailRef.current;
        }
        return mailApi
            .doUploadAttachment({
            cid: currentMailIdRef.current,
            _account: flag._account || '',
            attach: imgFile,
            uploader: undefined,
            flag,
            realId: mailApi.generateRndId(),
        })
            .then(({ fileUrl }) => {
            console.log('imagesUploadService', currentMailId);
            const imgSrc = proxyImg({ fileUrl, _token, isSubAccount });
            succFun({ url: `${imgSrc}&attachSize=${(imgFile === null || imgFile === void 0 ? void 0 : imgFile.size) || 0}`, originUrl: fileUrl });
            dispatch(doMailImgSizeChange({ count: (imgFile === null || imgFile === void 0 ? void 0 : imgFile.size) || 0 }));
            return fileUrl;
        })
            .catch(() => {
            failFun();
            return false;
        });
    };
    const debounceSendMail = useDebounceForEvent(() => {
        eventApi.sendSysEvent({
            eventName: 'mailMenuOper',
            eventData: {},
            eventStrData: 'sendMail',
        });
    }, 1000, {
        leading: true,
        trailing: false,
    });
    const getContact = (contactId, contactEmail) => {
        setContactClicked({ id: contactId, email: contactEmail });
        setContactCardVisible(true);
    };
    const edSetup = (ed) => {
        const fontColor = storeApi.getSync('defaultColor').data;
        if (fontColor) {
            setTimeout(() => {
                ed.fire('TextColorChange', { name: 'forecolor', color: fontColor });
            }, 2000);
        }
        ed.on('keydown', event => {
            if ((isMac && event.metaKey && event.key === 'Enter') || (!isMac && event.ctrlKey && event.key === 'Enter')) {
                event.preventDefault();
                debounceSendMail();
            }
            else if ((isMac && event.metaKey && event.key === 's') || (!isMac && event.ctrlKey && event.key === 's')) {
                event.preventDefault();
                texChangeDebounceSave();
            }
        });
        ed.on('focusout', () => {
            var _a, _b, _c, _d;
            const scrollBox = document.querySelector('#writeMailEditorScroll');
            const scrollBoxTop = (scrollBox === null || scrollBox === void 0 ? void 0 : scrollBox.scrollTop) || 0;
            dispatch(doChangeMailContent((_a = refEditorInstance === null || refEditorInstance === void 0 ? void 0 : refEditorInstance.current) === null || _a === void 0 ? void 0 : _a.getContent()));
            dispatch(doChangeMailScrollBoxTop(scrollBoxTop));
            const mail = Object.assign(Object.assign({}, JSON.parse(JSON.stringify(currentMail))), { _account: senderEmail });
            if ((_c = (_b = mail === null || mail === void 0 ? void 0 : mail.entry) === null || _b === void 0 ? void 0 : _b.content) === null || _c === void 0 ? void 0 : _c.content) {
                mail.entry.content.content = (_d = refEditorInstance === null || refEditorInstance === void 0 ? void 0 : refEditorInstance.current) === null || _d === void 0 ? void 0 : _d.getContent();
            }
            mailApi.doSaveMailLocal(mail);
        });
        ed.on('input', e => {
            if (!props.isContentChanged) {
                props.onEditChange();
            }
        });
        ed.on('focusout', () => {
            setTimeout(() => {
                setLayerZIndex(1);
            }, 300);
        });
        ed.on('clickContact', ({ top, left, bottom, right, contactId, email }) => {
            getContact(contactId, email);
            setContactPos({
                top,
                left,
                bottom,
                right,
            });
        });
        ed.on('ResizeContent', () => {
            handleSensitiveMarksUpdate('ResizeContent');
            setTimeout(() => {
                checkToolbarHeightChange();
            }, 0);
        });
        ed.on('ObjectResized', event => {
            handleSensitiveMarksUpdate('ObjectResized');
        });
        ed.on('ScrollContent', event => {
            var _a;
            if (enableSensitiveMarks && event.target === ((_a = refEditorInstance.current) === null || _a === void 0 ? void 0 : _a.getDoc())) {
                setSensitiveLayerScrollX(event.target.documentElement.scrollLeft);
            }
        });
    };
    const showContentAssistant = (status) => {
        showAssistant && showAssistant(status);
    };
    const edInitCallback = ed => {
        var _a;
        const onScaleCallback = (toMax) => {
            scale && scale(toMax);
            if (toMax) {
                contentAssitantEditor(showContentAssistant, !toMax, true);
            }
            else {
                contentAssitantEditor(showContentAssistant, sensitiveSetting.current, false);
            }
        };
        if (!isElectron) {
            scaleEditor(onScaleCallback);
        }
        if (!isMailTemplate && process.env.BUILD_ISEDM) {
            contentAssitantEditor(showContentAssistant);
        }
        const hasReceiver = ((_a = currentMail === null || currentMail === void 0 ? void 0 : currentMail.receiver) === null || _a === void 0 ? void 0 : _a.length) > 0;
        if (hasReceiver) {
            ed.focus();
        }
        setEditorInstance(ed);
        refEditorInstance.current = ed;
        onEditCreated && onEditCreated(ed);
        ed.fire('lxToggleSignEntry', { hide: hideSignEntryRef.current });
        getDefaultSign(false, (currentMail === null || currentMail === void 0 ? void 0 : currentMail.mailFormClickWriteMail) || senderEmail);
        writeMailLoadTrack();
        signLinkHandlers(ed, 'writeMail');
        setTimeout(() => {
            storeApi.get(SALES_PITCH_GUIDE_ID).then(({ suc, data }) => {
                const neverShown = !suc || data !== 'true';
                if (neverShown) {
                    const target = findActionNode();
                    if (inEdm && target != null) {
                        const { x, y, width, height } = target.getBoundingClientRect();
                        setWritePageGuidePos({
                            x,
                            y,
                            width,
                            height,
                        });
                        setGuideVisible(true);
                    }
                }
            });
        }, 1000);
    };
    const overLayer = () => {
        if (editorInstance) {
            setLayerZIndex(-1);
        }
    };
    const signatureActionAction = () => {
        selectSignModalAttr.show();
    };
    const salespitchActionAction = () => {
        setWritePageOuterDrawerVisible(true);
    };
    const getAtResult = (contactModel) => {
        var _a, _b;
        if (typeof contactModel === 'string') {
            getAtResultCBRef.current(contactModel);
            return;
        }
        getAtResultCBRef.current(((_a = contactModel.contactInfo) === null || _a === void 0 ? void 0 : _a[0]) || ((_b = contactModel.contact) === null || _b === void 0 ? void 0 : _b.accountName), contactModel.contact);
        const receiver = transContactModel2MailContactModel(contactModel, 'to');
        dispatch(doModifyReceiver({
            receiver: [receiver],
            receiverType: 'to',
            operation: 'paste',
        }));
        dispatch(ContactActions.doFocusSelector('to'));
    };
    const lastUse = (data) => {
        storeApi.put(data.key, data.value);
    };
    const uploadFromOfficeDocAction = () => {
        trackApi.track('pcMail_click_attachments_writeMailPage', { attachmentsList: getIn18Text('CONGYUNWENDANGTIAN1') });
        dispatch(DiskAttActions.doToggleDiskModal(true));
    };
    const uploadFromNormalAtt = () => {
        trackApi.track('pcMail_click_attachments_writeMailPage', { attachmentsList: getIn18Text('CONGWANGLAIFUJIAN') });
        dispatch(DiskAttActions.doSwitchType('normalAtt'));
        dispatch(DiskAttActions.doToggleDiskModal(true));
    };
    const lxContactShow = ({ position }, callback) => {
        setAtVisible(true);
        setContactPos(position);
        getAtResultCBRef.current = callback;
    };
    const comMailFormatAction = (formatId) => {
        changeShowTemplateList({ isShow: true, defaultActiveTab: formatId, templateType: 'write' });
    };
    const scale = (status) => {
        webScale && webScale();
    };
    const editorCk = () => {
        if (focusedRef.current !== getIn18Text('ZHENGWEN')) {
            setTimeout(() => {
                dispatch(ContactActions.doFocusSelector(getIn18Text('ZHENGWEN')));
            }, 100);
        }
        if (conferenceSetting) {
            setTimeout(() => {
                dispatch(doConferenceSettting(false));
            }, 100);
        }
        if (taskSetting) {
            setTimeout(() => {
                dispatch(doTaskMailSetting(false));
            }, 100);
        }
        if (praiseMailSetting) {
            setTimeout(() => {
                dispatch(doPraiseMailSetting(false));
            }, 100);
        }
    };
    const appendProductAction = () => {
        var _a;
        setShowSettingProductModal(true);
        trackApi.track('mail_add_product');
        (_a = props.setSiteLinkButtonVisible) === null || _a === void 0 ? void 0 : _a.call(props, false);
    };
    const appendLandingPageAction = () => {
        var _a;
        setShowSettingLandingModal(true);
        trackApi.track('market_landing_click', { from_page: 'mail' });
        (_a = props.setSiteLinkButtonVisible) === null || _a === void 0 ? void 0 : _a.call(props, false);
    };
    const handleVariableChange = (key, value) => {
        editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.execCommand('appendVar', false, { key, value });
    };
    const closeSettingProductModal = () => {
        setShowSettingProductModal(false);
        setFromImgProduct(false);
    };
    const closeSettingLandingModal = () => {
        setShowSettingLandingModal(false);
    };
    const setWriteMailSpin = () => {
        dispatch(doSetWirteMailSpin(false));
    };
    const handleAddProducts = (tab, lists, columns, fieldsMap, imgSize, langVal, imgDisplay) => {
        const html = getProductsHtml(tab, lists, columns, fieldsMap, imgSize, langVal, imgDisplay);
        editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.undoManager.transact(function () {
            editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.insertContent(html);
            if (fromImgProduct) {
                trackApi.track('pc_mail_picture_guide_click', { type: 1, action: 4 });
            }
            handleSensitiveMarksUpdate('handleAddProducts');
        });
        trackApi.track('email_addgoods_done', { position: 'mail' });
    };
    const handleAddLandingPage = (lists, imgSize) => {
        const html = getLandingPageHtml(lists, imgSize);
        editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.undoManager.transact(function () {
            editorInstance === null || editorInstance === void 0 ? void 0 : editorInstance.insertContent(html);
        });
    };
    const textareaChange = () => {
        if (textareaRef.current) {
            const value = textareaRef.current.value;
            dispatch(doChangeMailContent(value));
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 20 + 'px';
        }
    };
    useEffect(() => {
        if (!refContainer.current || !editorInstance)
            return;
        const toxSidebarWrap = refContainer.current.querySelector('.tox-sidebar-wrap');
        if (puretext) {
            toxSidebarWrap.style.display = 'none';
            textareaChange();
        }
        else {
            currentMailContent && editorInstance.setContent(currentMailContent.replace(/[\r\n]/g, '<br />'));
            editorInstance.fire('updatePlaceholder', { show: false });
            toxSidebarWrap.style.display = 'flex';
        }
    }, [puretext]);
    const billShow = (modalType) => {
        setReceiptConfig({
            modalType,
            trackType: 'mail',
            open: true,
        });
    };
    const billAsAttachment = useCallback((files) => {
        dispatch(DiskAttActions.doSetSelectedKeys(files));
        dispatch(doAddDiskAttachment({
            files,
            addType: 'normal',
            mailId: currentMailId,
        }));
    }, [currentMailId, dispatch]);
    const closeSelectReceiptModal = useCallback(() => {
        setReceiptConfig({
            modalType: undefined,
            trackType: 'mail',
            open: false,
        });
    }, []);
    const submitSelectReceipt = useCallback(e => {
        console.log('submitSelectReceipt', e);
        if (Array.isArray(e)) {
            const list = e.filter(item => item.netId);
            list.forEach(item => {
                item.id = item.netId;
                item.size = item.size || 0;
            });
            list.length > 0 && billAsAttachment(list);
        }
    }, [billAsAttachment]);
    const showAIAssistant = useFeatureVisibility('mail_ai_forbidden_domains');
    const filteredToolbar = useMemo(() => {
        try {
            if (!showAIAssistant) {
                const res = [...toolbar];
                res[0] = res[0].replace(/lxaiwritemail\s+(?:\$split\s*)?/g, '').trim();
                return res;
            }
            return toolbar;
        }
        catch (error) {
            console.log('Toolbar filtering error:', error);
            return toolbar;
        }
    }, [toolbar, showAIAssistant]);
    if ((!haveContent || !uploadAttachmentConfig) && !loadEd) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classnames([MAIL_EDITOR_CONTAINER_CLASS, inEdm && MAIL_EDITOR_CONTAINER_EDM_CLASS]), ref: refContainer },
            React.createElement("div", { className: "editor-sensitive-layer", style: { marginLeft: -1 * (sensitiveLayerScrollX || 0) } },
                React.createElement("div", { className: "editor-sensitive-content" }, enableSensitiveMarks &&
                    sensitiveMarks.map((item, index) => (React.createElement("div", { className: "editor-sensitive-mark", style: {
                            top: item.top,
                            left: item.left,
                            width: item.width,
                            height: item.height,
                        }, key: index }, item.isFirstNode && (React.createElement("div", { className: "editor-sensitive-mark-index", style: { height: 12, top: -12, display: 'flex', padding: '0 5px', alignItems: 'center' } }, getSensitiveDetectedIndex(item.word)))))))),
            env !== 'development' && React.createElement("div", { className: "editor-container-layer", onMouseMove: overLayer, style: { zIndex: layerZIndex } }),
            React.createElement("textarea", { ref: textareaRef, className: "editor-container-textarea", hidden: !puretext, value: currentMailContent, onInput: textareaChange }),
            React.createElement("div", { className: classnames({ 'control-editor-height': puretext }) },
                React.createElement(LxEditor, { onClick: editorCk, initialValue: content, init: {
                        from: 'mailWrite',
                        placeholder: getIn18Text('CHANGSHI@TA'),
                        content_style: tinyStyle,
                        recognizeDiskUrl,
                        setWriteMailSpin,
                        setup: edSetup,
                        getToken,
                        uploadAttachmentAction,
                        AntdMessage: message,
                        uploadFromOfficeDocAction,
                        uploadAttachment,
                        uploadAttachmentWithFiles,
                        mailFormatAction,
                        comMailFormatAction,
                        lxContactShow,
                        signatureActionAction,
                        insertTemplateToEditor,
                        salespitchActionAction,
                        pasteAsAttachment: () => {
                            pasteAtt && pasteAtt();
                        },
                        contextmenu: editor => {
                            const res = editor.selection.getContent()
                                ? 'cut copy paste pastetext lxcontextchangeimage | link linkchecker image imagetools spellchecker configurepermanentpen lximg | table | lxcontextOptimize'
                                : 'paste pasteasattach pastetext | link linkchecker image imagetools spellchecker configurepermanentpen lximg | table';
                            return res;
                        },
                        init_instance_callback: edInitCallback,
                        scrollSelect: '#writeMailEditorScroll',
                        images_upload_handler: imagesUploadHandler,
                        automatic_uploads: false,
                        last_use: lastUse,
                        uploadAttachmentConfig,
                        uploadFromNormalAtt,
                        min_height: 80,
                        tabIndex: 35,
                        toolbar_mode,
                        toolbar_scale: !isElectron,
                        onVaribleClickAction: props.onVaribleClickAction,
                        appendProductAction,
                        appendLandingPageAction,
                        showProductTipAction: props.showProductTipActionFunc,
                        onSiteLinkButtonEntryClick: props.onSiteLinkButtonEntryClickFunc,
                        toolbar: filteredToolbar,
                        billShow,
                    }, source: 'general', mailLangType: (_a = currentMail === null || currentMail === void 0 ? void 0 : currentMail.entry) === null || _a === void 0 ? void 0 : _a.langType, originMailId: currentMail === null || currentMail === void 0 ? void 0 : currentMail.originMailId, onEditorChange: changeEditor, lazy: true })),
            React.createElement(SelectSignModal, { signSelectId: "selectSignOnWrite", signEditId: "signEditOnWrite", writeType: currentMail.writeType, onSave: editorChangeSign }),
            React.createElement(SignEditModal, { signEditId: "signEditOnWrite" }),
            React.createElement(DiskAttachmentModal, null),
            React.createElement(LxPopover, { top: contactPos.top, left: contactPos.left, right: contactPos.right, bottom: contactPos.bottom, visible: atVisible, setVisible: setAtVisible, height: 246, acceptTopBottom: true },
                React.createElement(MailAt, { visible: atVisible, setVisible: setAtVisible, emitResult: getAtResult })),
            React.createElement(LxPopover, { top: contactPos.top, left: contactPos.left, right: contactPos.right, bottom: contactPos.bottom, visible: contactCardVisible, setVisible: setContactCardVisible, offset: [0, 30] }, contactClicked && (React.createElement(ContactDetail, { contactId: contactClicked.id, email: contactClicked.email, dividerLine: false, onNotifyParent: () => {
                    setContactCardVisible(false);
                }, branch: true, toolipShow: true }))),
            process.env.BUILD_ISEDM ? (React.createElement(ProductSettingModal, { fromPage: "mail", getContainer: ".writePage-module-container", onShow: appendProductAction, onAdd: handleAddProducts, visible: showSettingProductModal, onClose: closeSettingProductModal })) : null,
            process.env.BUILD_ISEDM ? (React.createElement(LandingPageModal, { fromPage: "mail", getContainer: ".writePage-module-container", onShow: appendLandingPageAction, onAdd: handleAddLandingPage, visible: showSettingLandingModal, onClose: closeSettingLandingModal })) : null,
            refContainer.current && process.env.BUILD_ISEDM && (React.createElement(ImgProduct, { top: 96, left: 16, from: 1, container: document.querySelector('.class-for-imgproduct'), visible: imgProductVisible, appendProductAction: appendProductAction, setVisible: setImgProductVisible })),
            React.createElement(SalesPitchWritePageDrawer, null),
            showSiteGuideState && (React.createElement(Popover, { content: React.createElement(SignSiteGuide, { signModalId: "selectSignOnWrite", onClose: () => {
                        setShowSiteGuideState(false);
                    } }), visible: true, placement: "rightTop", overlayClassName: "sign-site-guide-popover", getPopupContainer: node => node, autoAdjustOverflow: false },
                React.createElement("div", { style: { position: 'absolute', left: signDOMRect === null || signDOMRect === void 0 ? void 0 : signDOMRect.x, top: signDOMRect === null || signDOMRect === void 0 ? void 0 : signDOMRect.y, width: signDOMRect === null || signDOMRect === void 0 ? void 0 : signDOMRect.width, height: signDOMRect === null || signDOMRect === void 0 ? void 0 : signDOMRect.height } }))),
            process.env.BUILD_ISEDM && (React.createElement(SelectReceiptModal, { open: receiptConfig.open, trackType: 'mail', modalType: receiptConfig.modalType, onClose: closeSelectReceiptModal, onSubmit: submitSelectReceipt })))));
});
export default MailContent;
